import { z } from 'zod';

import { HUBSPOT_IMPORT_DEFAULT_NAME_COLUMN } from './constants';

/**
 * Shared with the file step's pre-submit check, so what counts as a valid
 * export can't drift between the two call sites.
 */
export const zodHubspotImportFileSchema = z
  .file()
  // Windows reports a `.csv` as `application/vnd.ms-excel` often enough to be
  // annoying, but accepting that type would let `.xls` through too, which the
  // backend cannot read. The file input's `accept` is the primary gate; this
  // check only catches an obviously wrong file before the round trip.
  .mime([
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
    'application/csv',
  ])
  .max(20 * 1024 * 1024); // 20 MB

export const zodHubspotImportMatchRequestSchema = z.object({
  customerNumberColumn: z
    .union([z.string().trim().min(1), z.literal('')])
    .nullable(),
  file: zodHubspotImportFileSchema,
  nameColumn: z
    .string()
    .trim()
    .min(1)
    .optional()
    .default(HUBSPOT_IMPORT_DEFAULT_NAME_COLUMN),
});

/**
 * `MatchedCompanyAccountRef` in the OpenAPI spec.
 */
export const zodHubspotImportMatchedCompanyAccountSchema = z.object({
  id: z.uuid(),
  name: z.string(),
});

/**
 * `MatchedCompanyRef` in the OpenAPI spec — a company the backend matched a
 * HubSpot deal name against. This is a slim projection of the company entity
 * — not `zodCompanyAdminSchema` — and it names the blacklist flag
 * `isBlacklisted`, where the company endpoints call it `blacklisted`.
 */
export const zodHubspotImportMatchedCompanySchema = z.object({
  companyAccount: zodHubspotImportMatchedCompanyAccountSchema,
  customerNumbers: z.array(z.string()),
  id: z.uuid(),
  isActive: z.boolean(),
  isBlacklisted: z.boolean(),
  /** Whether this company is already a relevant supplier of the buyer company. */
  isRelevant: z.boolean(),
  isSender: z.boolean(),
  name: z.string(),
});

/**
 * One row of the uploaded sheet together with the company the backend matched
 * it to. `match` is nullish because a row the backend could not match at all
 * carries no company — and with it no score.
 */
export const zodHubspotImportMatchSchema = z.object({
  customerNumber: z.string().nullish(),
  inputCompanyName: z.string(),
  match: z
    .object({
      company: zodHubspotImportMatchedCompanySchema,
      score: z.number().int().gte(0).lte(100),
    })
    .nullish(),
});

/**
 * The backend no longer groups the matches by confidence — it returns one flat
 * list and leaves the split to the score. See `HIGH_CONFIDENCE_SCORE_THRESHOLD`.
 */
export const zodHubspotImportMatchResponseSchema = z.object({
  data: z.array(zodHubspotImportMatchSchema),
});

export const zodHubspotImportMatchEndpointSchema = z.tuple([
  z.literal('vadmin'),
  z.literal('company'),
  z.uuid(),
  z.literal('hubspot_import'),
]);
